import { useState } from 'react';
import { Plus, Trash2, Copy, Check, Ban, Key } from 'lucide-react';
import {
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
  useDeleteApiKey,
} from '@/hooks/useApiKeys';
import { Button, Card, CardHeader, CardBody, Modal, ConfirmDialog, Input, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { ApiKey, ApiKeyCreated } from '@/types';
import { formatDistanceToNow } from 'date-fns';

export default function ApiKeySettings() {
  const { data: keys, isLoading } = useApiKeys();
  const [showCreate, setShowCreate] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="API Keys"
          subtitle="Create API keys to authenticate with the Sales API from scripts or external integrations. Keys are scoped to your user account."
          actions={
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="w-3.5 h-3.5" />
              Create Key
            </Button>
          }
        />

        <CardBody className="space-y-2">
          {(keys ?? []).length === 0 && (
            <p className="text-sm text-grey-500 text-center py-8">
              No API keys yet. Create one to get started.
            </p>
          )}
          {(keys ?? []).map((apiKey) => (
            <ApiKeyRow key={apiKey.id} apiKey={apiKey} />
          ))}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Usage" />
        <CardBody>
          <p className="text-sm text-grey-600 mb-3">
            Include the <code className="bg-grey-100 px-1.5 py-0.5 rounded text-xs font-mono">X-API-Key</code> header in your requests:
          </p>
          <pre className="bg-grey-50 border border-grey-200 rounded-lg p-4 text-xs font-mono text-grey-700 overflow-x-auto">
{`curl -H "X-API-Key: your-api-key" \\
  https://sales-api.soliddigital.com/api/v1/contacts`}
          </pre>
        </CardBody>
      </Card>

      {showCreate && (
        <CreateApiKeyDialog
          open={showCreate}
          onOpenChange={setShowCreate}
          onCreated={(key) => {
            setShowCreate(false);
            setCreatedKey(key);
          }}
        />
      )}

      {createdKey && (
        <KeyRevealDialog
          apiKey={createdKey}
          open={!!createdKey}
          onOpenChange={(open) => !open && setCreatedKey(null)}
        />
      )}
    </div>
  );
}

function ApiKeyRow({ apiKey }: { apiKey: ApiKey }) {
  const revokeKey = useRevokeApiKey();
  const deleteKey = useDeleteApiKey();
  const [confirming, setConfirming] = useState<'revoke' | 'delete' | null>(null);

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-grey-50 group">
      <ConfirmDialog
        open={confirming === 'revoke'}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={`Revoke "${apiKey.name}"?`}
        description="This will immediately invalidate the key."
        confirmLabel="Revoke"
        destructive
        onConfirm={() => revokeKey.mutateAsync(apiKey.id)}
      />
      <ConfirmDialog
        open={confirming === 'delete'}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={`Permanently delete "${apiKey.name}"?`}
        description="This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={() => deleteKey.mutateAsync(apiKey.id)}
      />
      <Key className="w-4 h-4 text-grey-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-secondary">{apiKey.name}</span>
          <code className="text-xs font-mono text-grey-500">{apiKey.prefix}...</code>
          {apiKey.isActive ? (
            <Badge variant="success" size="sm">Active</Badge>
          ) : (
            <Badge variant="default" size="sm">Revoked</Badge>
          )}
          {apiKey.expiresAt && new Date(apiKey.expiresAt) < new Date() && (
            <Badge variant="warning" size="sm">Expired</Badge>
          )}
        </div>
        <div className="text-xs text-grey-500 mt-0.5 flex gap-3">
          <span>Created {formatDistanceToNow(new Date(apiKey.createdAt), { addSuffix: true })}</span>
          {apiKey.lastUsedAt && (
            <span>Last used {formatDistanceToNow(new Date(apiKey.lastUsedAt), { addSuffix: true })}</span>
          )}
          {apiKey.expiresAt && (
            <span>Expires {formatDistanceToNow(new Date(apiKey.expiresAt), { addSuffix: true })}</span>
          )}
        </div>
      </div>
      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        {apiKey.isActive && (
          <button
            type="button"
            onClick={() => setConfirming('revoke')}
            aria-label="Revoke key"
            className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-warning"
          >
            <Ban className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setConfirming('delete')}
          aria-label="Delete key"
          className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-error"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function CreateApiKeyDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (key: ApiKeyCreated) => void;
}) {
  const [form, setForm] = useState({ name: '', expiresAt: '' });
  const createKey = useCreateApiKey();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await createKey.mutateAsync({
      name: form.name,
      expiresAt: form.expiresAt || undefined,
    });
    onCreated(result);
    setForm({ name: '', expiresAt: '' });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Create API Key"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="create-api-key-form" loading={createKey.isPending}>
            Create Key
          </Button>
        </>
      }
    >
      <form id="create-api-key-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          required
          placeholder="e.g. CI/CD Pipeline"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label="Expires at (optional)"
          type="date"
          value={form.expiresAt}
          onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
        />
      </form>
    </Modal>
  );
}

function KeyRevealDialog({
  apiKey,
  open,
  onOpenChange,
}: {
  apiKey: ApiKeyCreated;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(apiKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="API Key Created"
      footer={<Button onClick={() => onOpenChange(false)}>Done</Button>}
    >
      <div className="space-y-4">
        <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <p className="text-sm text-warning font-medium">
            Copy this key now. You won't be able to see it again.
          </p>
        </div>

        <div>
          <label className="text-sm font-medium text-grey-700 mb-1 block">
            {apiKey.name}
          </label>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-grey-50 border border-grey-200 rounded-lg px-3 py-2 text-sm font-mono break-all select-all">
              {apiKey.key}
            </code>
            <Button variant="outline" size="sm" onClick={handleCopy} aria-label={copied ? 'Copied' : 'Copy key'}>
              {copied ? (
                <Check className="w-4 h-4 text-success" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
